import { Trans } from '@lingui/react/macro';
import {
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '@repo/videa-components/src/ui/hover-card';
import { differenceInYears } from 'date-fns';

import type { TVisitSummary } from '../../../../react-query/useVisitSummaries';
import { useRblSeverities } from '../../../../utils/hooks/useRblSeverities';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { QuadrantPerioIndicator } from '../findings/findingChip';

export const RBLCloverPill = ({
  dateOfBirth,
  visitSummary,
}: {
  dateOfBirth?: string;
  visitSummary?: TVisitSummary;
}) => {
  const { trackEvent } = useTrackEvent();
  const severityStrings = useRblSeverities(visitSummary);
  const age = dateOfBirth
    ? differenceInYears(new Date(), new Date(dateOfBirth))
    : null;
  const isChild = typeof age === 'number' && age < 18;

  // Children can't really have bone loss yet, so these findings are mostly
  // angulation issues.
  if (!severityStrings || isChild) return null;

  return (
    <HoverCard
      openDelay={100}
      closeDelay={100}
      onOpenChange={(open) => {
        if (open) {
          trackEvent(mixpanelEvents.userHoverRblPill, {
            estimatedVisitId: visitSummary?.estimatedVisitId,
            unifiedId: visitSummary?.unifiedAppointmentId,
          });
        }
      }}
    >
      <HoverCardTrigger className="vai-self-start">
        <div className="vai-bg-slate-200 vai-rounded-3xl vai-py-1 vai-pl-2 vai-pr-3 vai-flex vai-items-center vai-gap-1">
          <p className="vai-typography-small !vai-text-[12px]">
            <Trans>RBL</Trans>
          </p>
          <QuadrantPerioIndicator
            quadrantColors={severityStrings}
            isScheduleViewEnabled={false}
          />
        </div>
      </HoverCardTrigger>
      {/* The HoverCardPortal ensures that the HoverCardContent is rendered at the root level of the DOM to avoid z-index issues. */}
      <HoverCardPortal>
        <HoverCardContent className="vai-w-96">
          <p className="vai-typography-p">
            <Trans>
              Overview of most severe bone level measured per quadrant (most
              recent set of images); does not necessarily indicate need for SRP
              or other treatment
            </Trans>
          </p>
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
};
